import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatError } from "@/lib/api";
import Logo from "@/components/portal/Logo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend sets the HttpOnly session cookie in its response.
      // The frontend never sees or stores the token itself.
      await api.post("/admin/login", { email, password });
      toast.success("Welcome back", { position: "bottom-center" });
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      <header className="px-6 sm:px-10 pt-8">
        <div className="flex justify-center">
          <Logo size="sm" withMonogram={false} />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <p className="overline">Staff Access</p>
            <h2 className="font-serif font-light text-4xl text-ink mt-3">Atelier Dashboard</h2>
            <div className="divider-thin" />
            <p className="text-xs text-muted2">Restricted to Tanish Textiles staff.</p>
          </div>

          <form
            onSubmit={submit}
            className="bg-white border border-line rounded-3xl p-8 shadow-[0_8px_40px_rgba(44,26,29,0.05)]"
          >
            <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">Email</label>
            <input
              data-testid="admin-email-input"
              type="email"
              autoComplete="username"
              className="luxury-input mb-5"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@tanishtextiles.com"
              required
            />
            <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">Password</label>
            <div className="relative mb-8">
              <input
                data-testid="admin-password-input"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="luxury-input !pr-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                data-testid="admin-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted2 hover:text-maroon transition-colors p-1"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff size={16} strokeWidth={1.6} />
                ) : (
                  <Eye size={16} strokeWidth={1.6} />
                )}
              </button>
            </div>
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="btn-maroon w-full"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Lock size={16} strokeWidth={1.6} /> Sign in</>}
            </button>
          </form>

          <p className="text-center text-[11px] uppercase tracking-[0.24em] text-muted2 mt-8">
            <button onClick={() => navigate("/")} className="hover:text-maroon transition-colors" data-testid="admin-back-home">
              ← Return to portal
            </button>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
